import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowUpRight } from 'lucide-react';

const SectorsPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const sectors = [
    // CITY FIRST
    {
      id: 'city',
      name: 'City',
      tagline: 'Professional Services & Finance',
      description: 'We partner with law firms, investment funds, and professional services organisations to create sophisticated brand experiences. Our work in this sector combines gravitas with innovation.',
      image: '/images/clients/Brandsmiths Background - City.jpg',
      clients: [
        { name: 'Brandsmiths', role: 'Award-Winning Law Firm', services: 'Website, Video, Social, Marketing', image: '/images/clients/Brandsmiths Background - City.jpg', logo: '/images/clients/Brandsmiths Logo.png', logoSize: 'large', link: 'https://brandsmiths.co.uk/', gradient: 'from-[#E91E63]/40 via-transparent to-transparent', gradientDirection: 'bg-gradient-to-t' },
        { name: 'NicheCom', role: 'Visual Property Marketing', services: 'Website, Branding, Marketing', image: '/images/clients/NicheCom.png', logo: '/images/clients/Nichecom-Logo-White.png', gradient: 'from-[#4CAF50]/60 via-transparent to-transparent', gradientDirection: 'bg-gradient-to-b' },
        { name: 'VenCap', role: 'Investment Management', services: 'Development', image: '/images/clients/Vencap background - City.png', logo: '/images/clients/VenCap Logo.png' },
        { name: 'Hagen Wolf', role: 'Award-Winning Law Firm', services: 'Website, Marketing', image: '/images/clients/HAgen Wolf Background - City.png', logo: '/images/clients/HAgen Wolf Logo.png', link: 'https://hagenwolf.co.uk/' },
        { name: 'KKV Investment Fund', role: 'Investment Fund', services: 'Website, Press, Marketing', image: '/images/clients/obgImage-1551195.jpg', logo: '/images/clients/KKV Logo.png' },
        { name: 'LTS Group', role: 'SEND Recruitment', services: 'Website, Social, Marketing', image: '/images/clients/LTS Background - City.jpg', logo: '/images/clients/LTS Group Logo.png', logoSize: 'large' },
      ],
    },
    {
      id: 'sport',
      name: 'Sport',
      tagline: 'Elevating Athletes & Teams',
      description: 'We work with elite athletes, teams, and sports organisations to build powerful personal brands and drive commercial success. From heavyweight boxers to F1 drivers, we understand the unique demands of sports marketing.',
      image: '/images/clients/laurence okolie world title.jpg',
      clients: [
        { name: 'Callum Ilott', role: 'F1 Racing Driver', services: 'Website Development', image: '/images/clients/Callum Ilott - Sport.jpg', link: 'https://callumilott.com/' },
        { name: 'Lawrence Okolie', role: 'World Champion Boxer', services: 'Brand Development', image: '/images/clients/laurence okolie world title.jpg' },
        { name: 'Joe Joyce', role: 'Heavyweight Boxer', services: 'Social, Video, Website, Merchandise', image: '/images/clients/Joe Joyce - sport.jpg', link: 'https://joejoyceboxing.com/' },
        { name: 'Johnny Fisher', role: 'Heavyweight Boxer', services: 'Merchandise', image: '/images/clients/Johnny fisher - Sport.jpg' },
      ],
    },
    {
      id: 'music',
      name: 'Music',
      tagline: 'Artists & Composers',
      description: 'From emerging talent to established artists, we help musicians and composers build lasting connections with their audiences. We understand the music industry and create work that resonates.',
      image: '/images/clients/stilesdrewe.jpg',
      clients: [
        { name: 'Stiles & Drewe', role: 'British Theatre Composers', services: 'Website, Store, Imagery', image: '/images/clients/stilesdrewe.jpg' },
        { name: 'Kerry Ellis', role: 'West End Star', services: 'Website, Branding', image: '/images/clients/Kerry Ellis.jpg' },
        { name: 'Mark Kingswood', role: 'Music Artist', services: 'Website, Music Videos', image: '/images/clients/Mark Kingswood - Music.jpg' },
      ],
    },
    {
      id: 'entertainment',
      name: 'Entertainment',
      tagline: 'Theatre & Live Events',
      description: 'We bring entertainment properties to life, from beloved children\'s characters to West End productions. Our work drives ticket sales and creates unforgettable brand experiences.',
      image: '/images/clients/Teletubbies Entertainment.jpg',
      clients: [
        { name: 'Teletubbies', role: '1st Ever Theatre Tour', services: 'Campaign', image: '/images/clients/Teletubbies Entertainment.jpg' },
        { name: 'Tom Gates', role: 'National Tour', services: 'Website', image: '/images/clients/Brilliant World Of Tom Gates - Entertainment.jpg' },
        { name: 'Fat Friends', role: 'UK Tour', services: 'Website', image: '/images/clients/Fat Friends The Musical - Entertainment.jpg' },
        { name: 'Fireman Sam', role: 'National Tour', services: 'Website', image: '/images/clients/Fireman Sam - entertainment.jpg' },
        { name: 'Doctor Dolittle', role: 'Theatre Production', services: 'Campaign', image: '/images/clients/Doctor Doolittle - entertainment.jpg' },
      ],
    },
  ];

  return (
    <div data-testid="sectors-page">
      {/* Hero */}
      <section className="min-h-[50vh] flex items-center relative overflow-hidden bg-elate-surface">
        <div className="container-custom relative z-10 pt-32 pb-20">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Sectors
          </motion.p>

          <motion.h1
            className="text-headline text-white mb-8 max-w-4xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Specialist
            <br />
            <span className="text-elate-yellow">Expertise</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            We've built deep expertise in four key sectors over 15+ years. Understanding the nuances of each industry allows us to deliver work that truly resonates—now supercharged with AI.
          </motion.p>
        </div>
      </section>

      {/* Sector Sections */}
      {sectors.map((sector, index) => (
        <section
          key={sector.id}
          id={sector.id}
          className={`section-padding ${index % 2 === 0 ? 'bg-elate-black' : 'bg-elate-surface'}`}
          data-testid={`sector-${sector.id}`}
        >
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <motion.div
                className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={sector.image}
                    alt={sector.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-elate-yellow flex items-center justify-center">
                  <span className="text-2xl font-heading text-elate-black">
                    #{sector.name.toLowerCase().charAt(0)}
                  </span>
                </div>
              </motion.div>

              {/* Content */}
              <motion.div
                className={index % 2 === 1 ? 'lg:order-1' : ''}
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <p className="caption text-elate-yellow mb-4">#{sector.name}</p>
                <h2 className="text-subhead text-white mb-4">
                  {sector.tagline}
                </h2>
                <p className="text-gray-400 leading-relaxed mb-8">
                  {sector.description}
                </p>

                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  Work with Us
                  <ArrowUpRight size={16} />
                </Link>
              </motion.div>
            </div>

            {/* Client Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm uppercase tracking-wider text-gray-500 mb-6">Selected Clients</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {sector.clients.map((client) => (
                  <div
                    key={client.name}
                    className="group relative aspect-square overflow-hidden"
                  >
                    {client.image ? (
                      <>
                        <img
                          src={client.image}
                          alt={client.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-elate-black via-elate-black/50 to-transparent" />
                        {/* Custom colour gradient overlay */}
                        {client.gradient && (
                          <div className={`absolute inset-0 ${client.gradientDirection} ${client.gradient}`} />
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full bg-elate-surface flex items-center justify-center">
                        {client.logo && (
                          <img
                            src={client.logo}
                            alt={client.name}
                            className="w-2/3 h-auto object-contain filter brightness-0 invert opacity-60"
                          />
                        )}
                      </div>
                    )}
                    
                    {/* Colour logo overlay for clients with logos and images */}
                    {client.logo && client.image && (
                      <div className="absolute top-3 left-3 right-3">
                        <img
                          src={client.logo}
                          alt=""
                          className={`w-auto object-contain drop-shadow-lg ${client.logoSize === 'large' ? 'h-8' : 'h-6'}`}
                        />
                      </div>
                    )}
                    
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white font-semibold text-sm truncate">{client.name}</p>
                      <p className="text-gray-400 text-xs truncate">{client.role}</p>
                    </div>
                    
                    {client.link && (
                      <a
                        href={client.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 z-10"
                        aria-label={`Visit ${client.name} website`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="section-padding bg-elate-yellow">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-subhead text-elate-black mb-6">
              Which Sector Are You In?
            </h2>
            <p className="text-elate-black/70 max-w-xl mx-auto mb-8 text-lg">
              Even if your industry isn't listed, our expertise translates. We'd love to hear about your project and explore how AI can transform your brand.
            </p>
            <Link
              to="/contact"
              className="bg-elate-black text-white font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 hover:bg-white hover:text-elate-black transition-colors"
            >
              Get in Touch
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SectorsPage;
